/**
 * versionGuard — auto-reload when a newer build is deployed, so a device never
 * gets stuck on a stale cached bundle (mobile Safari especially). This is a
 * standing SLIK resilience feature (Jose 2026-08-28: self-healing patterns are
 * always-a-go — apply the same guard across every customer/staff app).
 *
 * How it works: the running app's hashed main bundle (`main.<hash>.js`) is read
 * from the DOM. index.html is served `no-cache`, so we re-fetch it (`no-store`)
 * and compare the deployed hash. If it changed, a redeploy happened → reload to
 * the fresh build. Reloads ONLY at safe moments (tab re-focus, coming back
 * online, or an idle poll while the user isn't typing) so it never interrupts an
 * active checkout/booking.
 */

function runningBundle(): string | null {
  for (const s of Array.from(document.getElementsByTagName('script'))) {
    const m = (s.getAttribute('src') || '').match(/main\.[a-z0-9]+\.js/);
    if (m) return m[0];
  }
  return null;
}

function isTyping(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
}

export function initVersionGuard(pollMs = 90 * 1000): void {
  const current = runningBundle();
  if (!current) return; // dev server / unexpected shape → no-op
  let reloading = false;

  const check = async (force: boolean) => {
    if (reloading || document.hidden) return;
    if (!force && isTyping()) return; // don't yank the page out from under someone mid-typing
    try {
      const res = await fetch(`/index.html?cb=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const latest = ((await res.text()).match(/main\.[a-z0-9]+\.js/) || [])[0];
      if (latest && latest !== current) {
        reloading = true;
        window.location.reload();
      }
    } catch { /* offline / transient — retry next tick */ }
  };

  // Primary: user returns to the tab (safe — they weren't mid-action while it was hidden).
  document.addEventListener('visibilitychange', () => { if (!document.hidden) check(true); });
  window.addEventListener('online', () => check(true));
  // iOS Safari: returning via back/forward or the app-switcher restores the page from bfcache and
  // often does NOT fire visibilitychange — so the tab keeps showing the stale bundle. `pageshow`
  // (persisted=true = bfcache restore) + `focus` catch exactly that case. Jose 2026-09-06.
  window.addEventListener('pageshow', (e) => { if ((e as PageTransitionEvent).persisted) check(true); });
  window.addEventListener('focus', () => check(true));
  // Backup: idle poll, skipped while typing.
  setInterval(() => check(false), pollMs);
  // Immediate check on load (Jose 2026-09-18): heal a STALE cached bundle right away instead of
  // waiting up to pollMs for the first idle poll. No-op on a fresh load (hash matches).
  check(true);
}
